import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import { SiteHeader } from "@/components/landing/SiteHeader";
import { SiteFooter } from "@/components/landing/Footer";
import { FAQS } from "@/components/landing/Sections";
import { ContactForm } from "@/components/landing/ContactForm";
import { Button } from "@/components/ui/button";
import { EMAIL, EmailLink } from "@/components/EmailLink";
import { Markdown } from "@/components/Markdown";
import { useSiteContent, useSiteFaqs } from "@/pages/SitePages";

const legal = (title, sections) => ({ title, legal: true, sections });

const PAGES = {
  about: {
    title: "About Crowlearn",
    body: [
      "Crowlearn is a career readiness platform. We help job seekers, unemployed professionals, recent graduates and career changers build the skills employers ask for, and we help employers find candidates whose skills are verified.",
    ],
  },
  contact: {
    title: "Contact Crowlearn Services",
    body: ["Have a question about your account, a course or hiring on Crowlearn?"],
    emailButton: true,
    form: true,
  },
  privacy: legal("Privacy Policy", ["Who we are", "What information we collect", "How we use your information", "Who can see your profile", "How long we keep your data", "Your rights", "How to contact us"]),
  terms: legal("Terms of Service", ["About these terms", "Your account", "Using Crowlearn", "Employer accounts", "Content and intellectual property", "Ending your account", "Changes to these terms", "How to contact us"]),
  cookies: legal("Cookie Policy", ["What cookies are", "Cookies we use", "Managing cookies", "How to contact us"]),
  "learning-disclaimer": legal("Learning & Assessment Disclaimer", ["About our courses and assessments", "Skill verification levels", "Employment outcomes", "How to contact us"]),
  refunds: legal("Refund & Cancellation Policy", ["Course purchases", "Memberships and subscriptions", "Organisation plans", "How to request a refund", "How to contact us"]),
  help: { title: "Help Centre", body: ["Answers to common questions.", <>Can't find an answer? Email <EmailLink testid="help-email" /></>], faq: true },
  "account-billing": { title: "Account & Billing", body: [<>For billing questions, email <EmailLink testid="billing-email" /></>] },
};

export const PAGE_KEYS = Object.keys(PAGES);

const ContactLink = () => (
  <Link to="/contact" className="font-semibold text-[#222d99] underline underline-offset-4 hover:text-[#1a237e]" data-testid="info-contact-link">Contact Us</Link>
);

function LegalBody({ sections }) {
  return (
    <>
      <p className="mt-4 inline-block rounded-md border border-amber-400 bg-amber-50 px-3 py-1.5 text-sm font-semibold text-amber-900" data-testid="legal-draft-label">Draft – to be reviewed before launch</p>
      <ol className="mt-8 space-y-6">
        {sections.map((s, i) => (
          <li key={s}>
            <h2 className="text-lg font-semibold text-slate-900">{i + 1}. {s}</h2>
            <p className="mt-1 text-slate-700">[To be written]</p>
          </li>
        ))}
      </ol>
      <p className="mt-8 text-slate-700">Questions? <ContactLink /></p>
      <section className="mt-10 border-t border-slate-200 pt-6" data-testid="legal-contact-section">
        <h2 className="text-lg font-semibold text-slate-900">Contact</h2>
        <EmailLink testid="legal-email" />
      </section>
    </>
  );
}

const EmailButton = () => (
  <Button asChild size="lg" className="mt-8 h-12 rounded-full bg-[#222d99] px-8 hover:bg-[#1a237e] focus-visible:ring-2 focus-visible:ring-[#222d99] focus-visible:ring-offset-2" data-testid="contact-email-btn">
    <a href={`mailto:${EMAIL}`}><Mail className="mr-2 h-4 w-4" aria-hidden="true" /> Email us</a>
  </Button>
);

export default function InfoPage({ page }) {
  const p = PAGES[page];
  const c = useSiteContent(page);
  const faqs = useSiteFaqs(FAQS);
  const title = c?.title || p.title;
  useEffect(() => { document.title = c?.seo_title || `${title} | Crowlearn`; window.scrollTo(0, 0); }, [title, c]);
  const custom = !!c?.body;
  return (
    <div className="flex min-h-screen flex-col bg-white">
      <SiteHeader />
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-14 sm:px-6" data-testid={`info-page-${page}`}>
        <Link to="/" className="mb-6 inline-flex items-center gap-1 text-sm text-slate-700 hover:text-[#222d99]" data-testid="info-back-home"><ArrowLeft className="h-4 w-4" /> Home</Link>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl" data-testid="info-page-title">{title}</h1>
        {custom && (
          <div className="mt-6 text-base leading-relaxed text-slate-700" data-testid="info-page-custom">
            {p.legal && <p className="mb-4 text-sm text-slate-600" data-testid="legal-version">Version {c.version} · effective {String(c.publish_at || "").slice(0, 10)}</p>}
            <Markdown text={c.body} />
            {p.legal && <section className="mt-10 border-t border-slate-200 pt-6" data-testid="legal-contact-section"><h2 className="text-lg font-semibold text-slate-900">Contact</h2><EmailLink testid="legal-email" /></section>}
          </div>
        )}
        {!custom && p.body && (
          <div className="mt-6 space-y-4 text-base leading-relaxed text-slate-700">
            {p.body.map((t, i) => <p key={i}>{t}</p>)}
          </div>
        )}
        {!custom && p.legal && <LegalBody sections={p.sections} />}
        {p.emailButton && <EmailButton />}
        {p.form && <ContactForm />}
        {p.faq && (
          <dl className="mt-10 space-y-6">
            {faqs.map((f) => (
              <div key={f.q} className="rounded-xl border border-slate-200 p-5">
                <dt className="font-semibold text-slate-900">{f.q}</dt>
                <dd className="mt-2 text-slate-700">{f.a}</dd>
              </div>
            ))}
          </dl>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
